import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { compare } from 'fast-json-patch';
import { mergeMap } from 'rxjs';

import { safeMap } from '../effects.helpers';

import {
  AppleProfilePresetApiActions,
  AppleProfilePresetResolverActions,
  AppleProfilePresetViewActions,
} from './apple-profile-preset.actions';
import { APPLE_PROFILE_PRESET_CONFIGS } from './apple-profile-preset.registry';
import {
  AppleProfilePresetAddType,
  AppleProfilePresetType,
  AppleProfilePresetTypeConfig,
  PresetAddModelType,
} from './apple-profile-preset.types';

@Injectable()
export class AppleProfilePresetEffects {
  private readonly actions$ = inject(Actions);
  private readonly configs = inject(APPLE_PROFILE_PRESET_CONFIGS);

  private readonly configMap: Map<AppleProfilePresetType, AppleProfilePresetTypeConfig>;

  constructor() {
    this.configMap = new Map(this.configs.map((c) => [c.profileType, c]));
  }

  /**
   * Load all presets for a given profile type
   */
  presetsRequestedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppleProfilePresetResolverActions.presetsRequested),
      mergeMap(({ profileType }) => {
        const config = this.configMap.get(profileType);

        if (!config) {
          return [
            {
              ...AppleProfilePresetApiActions.presetsLoadedError({
                profileType,
                error: { message: `Unknown profile type: ${profileType}` },
              }),
              toast: {
                message: `[Error]: Unknown profile type: ${profileType}`,
                toastType: 'error',
                showNotification: true,
              },
            },
          ];
        }

        return safeMap(
          config.apiService.getAll(),
          (presets) => AppleProfilePresetApiActions.presetsLoaded({ profileType, presets }),
          (error) => AppleProfilePresetApiActions.presetsLoadedError({ profileType, error })
        );
      })
    );
  });

  /**
   * Add a new preset
   */
  presetAddedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppleProfilePresetViewActions.presetAdded),
      mergeMap(
        ({
          profileType,
          preset,
        }: {
          profileType: AppleProfilePresetAddType;
          preset: PresetAddModelType<AppleProfilePresetAddType>;
        }) => {
          const config = this.configMap.get(profileType);

          if (!config) {
            return [
              {
                ...AppleProfilePresetApiActions.presetAddedError({
                  profileType,
                  preset,
                  error: { message: `Unknown profile type: ${profileType}` },
                }),
                toast: {
                  message: `[Error]: Unknown profile type: ${profileType}`,
                  toastType: 'error',
                  showNotification: true,
                },
              },
            ];
          }

          return safeMap(
            config.apiService.add(preset),
            (addedPreset) => AppleProfilePresetApiActions.presetAdded({ profileType, preset: addedPreset }),
            (error) => AppleProfilePresetApiActions.presetAddedError({ profileType, preset, error })
          );
        }
      )
    );
  });

  /**
   * Update an existing preset
   */
  presetUpdatedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppleProfilePresetViewActions.presetUpdated),
      mergeMap(({ profileType, originalPreset, preset }) => {
        const config = this.configMap.get(profileType);

        if (!config) {
          return [
            {
              ...AppleProfilePresetApiActions.presetUpdatedError({
                profileType,
                preset: originalPreset,
                error: { message: `Unknown profile type: ${profileType}` },
              }),
              toast: {
                message: `[Error]: Unknown profile type: ${profileType}`,
                toastType: 'error',
                showNotification: true,
              },
            },
          ];
        }

        const patch = compare(originalPreset, preset);
        return safeMap(
          config.apiService.update(originalPreset.id, patch),
          () => AppleProfilePresetApiActions.presetUpdated({ profileType, preset }),
          (error) => AppleProfilePresetApiActions.presetUpdatedError({ profileType, preset: originalPreset, error })
        );
      })
    );
  });

  /**
   * Delete a preset
   */
  presetDeletedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppleProfilePresetViewActions.presetDeleted),
      mergeMap(({ profileType, preset }) => {
        const config = this.configMap.get(profileType);

        if (!config) {
          return [
            {
              ...AppleProfilePresetApiActions.presetDeletedError({
                profileType,
                preset,
                error: { message: `Unknown profile type: ${profileType}` },
              }),
              toast: {
                message: `[Error]: Unknown profile type: ${profileType}`,
                toastType: 'error',
                showNotification: true,
              },
            },
          ];
        }

        return safeMap(
          config.apiService.delete(preset.id),
          () => AppleProfilePresetApiActions.presetDeleted({ profileType, preset }),
          (error) => AppleProfilePresetApiActions.presetDeletedError({ profileType, preset, error })
        );
      })
    );
  });
}
